import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Mail, BookOpen } from 'lucide-react';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

type Status = 'verifying' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const [resending, setResending] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No verification token found. Please use the link from your email.');
      return;
    }

    authAPI.verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        setErrorMsg(
          err.response?.data?.error || 'Verification token is invalid or has expired.'
        );
      });
  }, [token]);

  const handleResend = async () => {
    setResending(true);
    try {
      await authAPI.resendVerification();
      toast.success('Verification email sent! Check your inbox.');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to resend. Please try again.';
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900">Mock Test Nation</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-secondary-100 p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader className="h-8 w-8 text-primary-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Verifying your email…</h2>
              <p className="text-secondary-500 text-sm">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Email verified!</h2>
              <p className="text-secondary-500 text-sm mb-6">
                Your email address has been confirmed. You can now sign in to your account.
              </p>
              <Link to="/login" className="btn-primary w-full btn-lg inline-block">
                Sign in now
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Verification failed</h2>
              <p className="text-secondary-500 text-sm mb-6">{errorMsg}</p>
              <div className="space-y-3">
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="btn-primary w-full btn-lg"
                >
                  {resending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="h-4 w-4 animate-spin" /> Sending…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Mail className="h-4 w-4" /> Resend verification email
                    </span>
                  )}
                </button>
                <Link to="/login" className="btn-secondary w-full btn-lg inline-block text-center">
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
